// The Objectives page's reference-window (weeks) toggle — option set, default and
// localStorage persistence. Pure, no Vue/store dependencies, so it is unit-testable in isolation the
// day a runner lands.
//
// Options and defaults differ per animal type (the global pig-type toggle), and the last-chosen
// value is remembered independently per type — switching the global toggle restores that type's own
// last choice (or its default, if it never had one) rather than carrying the other type's selection
// across.

type AnimalKey = 'weaner' | 'slaughterPig';

// One localStorage key per animal type, so a Weaner choice never leaks into SlaughterPig or vice versa.
const STORAGE_KEYS: Record<AnimalKey, string> = {
    weaner: 'objectives.referenceWindow.weaner',
    slaughterPig: 'objectives.referenceWindow.slaughterPig',
};

export const REFERENCE_WINDOW_OPTIONS: Record<AnimalKey, number[]> = {
    weaner: [1, 4, 8],
    slaughterPig: [1, 4, 13],
};

// Each type defaults to its own largest option.
export const REFERENCE_WINDOW_DEFAULT: Record<AnimalKey, number> = {
    weaner: 8,
    slaughterPig: 13,
};

function animalKey(isWeaner: boolean): AnimalKey {
    return isWeaner ? 'weaner' : 'slaughterPig';
}

export function getReferenceWindowOptions(isWeaner: boolean): number[] {
    return REFERENCE_WINDOW_OPTIONS[animalKey(isWeaner)];
}

export function getDefaultReferenceWindow(isWeaner: boolean): number {
    return REFERENCE_WINDOW_DEFAULT[animalKey(isWeaner)];
}

// Reads the stored weeks value for the given animal type, falling back to that type's default when
// nothing is stored yet — or when the stored value no longer belongs to that type's option set (e.g.
// a stale value from before an options-set change).
export function readStoredReferenceWindow(isWeaner: boolean): number {
    const options = getReferenceWindowOptions(isWeaner);
    const raw = window.localStorage.getItem(STORAGE_KEYS[animalKey(isWeaner)]);
    const parsed = raw !== null ? Number(raw) : NaN;
    return options.includes(parsed) ? parsed : getDefaultReferenceWindow(isWeaner);
}

export function writeStoredReferenceWindow(isWeaner: boolean, weeks: number): void {
    window.localStorage.setItem(STORAGE_KEYS[animalKey(isWeaner)], String(weeks));
}
